'use client';

/**
 * Cray26 Theme - Register Page
 * Modern registration page with Tailwind CSS styling
 */
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useAuthService } from '@/hooks/useAuthService';
import { useTranslation } from '@/lib/i18n';
import { logger } from '@/lib/logger';
import { IRegisterData } from '@/services/modules';
import { Input } from '@/themes/cray26/ui';

export default function RegisterPage() {
  // regular customers only
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    // enforced to REGULAR on this page
    customer_type: 'REGULAR' as const,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();
  const { t } = useTranslation();

  const authService = useAuthService();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const registerData: IRegisterData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        customer_type: 'REGULAR' as const,
      };

      await authService.register(registerData);

      logger.info({ msg: 'Registration successful', email: formData.email });
      router.push('/login?message=' + encodeURIComponent(t('common.success')) + '&type=success');
    } catch (err: any) {
      setError(err.message || t('common.error'));
      logger.error({ msg: 'Registration error', error: err });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{t('auth.createAccount')}</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link href="/login" className="font-medium text-primary-600 hover:text-primary-500">
            {t('auth.loginHere')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.businessSignUpPrompt')}{' '}
          <Link href="/register-business" className="font-medium text-primary-600 hover:text-primary-500">
            {t('auth.registerBusiness')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>}

            <Input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={formData.name}
              onChange={handleChange}
              label={t('auth.fullName')}
              placeholder={t('auth.fullNamePlaceholder')}
            />

            <Input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} label={t('auth.email')} placeholder={t('auth.emailPlaceholder')} />

            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={handleChange}
              label={t('auth.password')}
              placeholder={t('auth.passwordPlaceholder')}
            />

            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              label={t('auth.confirmPassword')}
              placeholder={t('auth.passwordPlaceholder')}
            />

            {/* regular customers don't need additional fields */}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                data-testid="register-submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
